import { distance } from "./eventUtils";

const INSET_PX = 24;
const COMPACT_INSET_PX = 10;
const COMPACT_MAX_WIDTH = 390;
const MIN_VISIBLE_MAP_PX = 96;

export const OVERVIEW_MAX_ZOOM = 16;
export const NEIGHBORHOOD_MAX_ZOOM = 15;
export const NEIGHBORHOOD_RADIUS_MILES = 0.4;
export const LIST_SCROLL_TOP_THRESHOLD = 8;
export const OVERVIEW_FALLBACK_ZOOM = 13;

export function getItemLatLng(item) {
  if (!item) return null;
  if (Number.isFinite(item.lat) && Number.isFinite(item.lng)) {
    return { lat: item.lat, lng: item.lng };
  }

  const loc =
    item.effectiveLocation ||
    item.location ||
    item.venueDetails?.location ||
    item.venue?.location ||
    item._matchingEvent?.effectiveLocation ||
    item._matchingEvent?.location ||
    item._matchingEvent?.venueDetails?.location ||
    item._matchingEvent?.venue?.location;
  const coordinates = loc?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;

  const lng = Number(coordinates[0]);
  const lat = Number(coordinates[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

export function getClosestMapVenues(userLocation, items, limit = 10) {
  const origin = getItemLatLng(userLocation);
  if (!origin || !Array.isArray(items)) return [];

  return items
    .map((item) => {
      const point = getItemLatLng(item);
      if (!point) return null;
      return {
        item,
        ...point,
        distance: distance(origin.lat, origin.lng, point.lat, point.lng),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit);
}

export function getNeighborhoodBounds(
  lat,
  lng,
  radiusMiles = NEIGHBORHOOD_RADIUS_MILES
) {
  const latDelta = radiusMiles / 69;
  const lngDelta =
    radiusMiles / (69 * Math.max(Math.cos((lat * Math.PI) / 180), 0.2));
  return [
    [lat - latDelta, lng - lngDelta],
    [lat + latDelta, lng + lngDelta],
  ];
}

function getChromeInset(isBottomSheet) {
  const narrow =
    typeof window !== "undefined" && window.innerWidth <= COMPACT_MAX_WIDTH;
  return isBottomSheet || narrow ? COMPACT_INSET_PX : INSET_PX;
}

export function getMapChromePadding(map) {
  const container = map?.getContainer?.();
  if (!container) {
    const inset = getChromeInset(false);
    return {
      paddingTopLeft: [inset, inset],
      paddingBottomRight: [inset, inset],
    };
  }

  const mapRect = container.getBoundingClientRect();
  const sheet = document.querySelector(".dynamic-bottom-sheet");
  const sheetRect = sheet?.getBoundingClientRect();
  const isLeftColumn =
    sheetRect &&
    sheetRect.left <= mapRect.left + 16 &&
    sheetRect.width < mapRect.width * 0.65;
  const isBottomSheet = Boolean(sheetRect) && !isLeftColumn;
  const inset = getChromeInset(isBottomSheet);

  let top = inset;
  let right = inset;
  let bottom = inset;
  let left = inset;

  const topBar = document.getElementById("map-top-bar");
  if (topBar) {
    const overlap = Math.max(0, topBar.getBoundingClientRect().bottom - mapRect.top);
    top = Math.max(top, overlap + inset);
  }

  if (sheetRect) {
    const overlapX =
      Math.min(sheetRect.right, mapRect.right) -
      Math.max(sheetRect.left, mapRect.left);
    const overlapY =
      Math.min(sheetRect.bottom, mapRect.bottom) -
      Math.max(sheetRect.top, mapRect.top);

    if (overlapX > 0 && overlapY > 0) {
      if (isLeftColumn) {
        left = Math.max(left, sheetRect.right - mapRect.left + inset);
      } else if (sheetRect.top > mapRect.top + mapRect.height * 0.2) {
        bottom = Math.max(bottom, mapRect.bottom - sheetRect.top + inset);
      }
    }
  }

  const maxBottom = Math.max(inset, mapRect.height - top - MIN_VISIBLE_MAP_PX);
  bottom = Math.min(bottom, maxBottom);

  return {
    paddingTopLeft: [left, top],
    paddingBottomRight: [right, bottom],
  };
}

let programmaticMoveId = 0;

export function runProgrammaticMapMove(map, moveFn, isProgrammaticMoveRef) {
  if (!map || !moveFn) return;
  const moveId = ++programmaticMoveId;
  if (isProgrammaticMoveRef) {
    isProgrammaticMoveRef.current = true;
  }

  // Only the latest move may clear the flag, so an earlier move's timeout
  // can't mark a newer programmatic move as user-driven.
  const finish = () => {
    map.off("moveend", finish);
    if (moveId !== programmaticMoveId) return;
    if (isProgrammaticMoveRef) {
      isProgrammaticMoveRef.current = false;
    }
  };

  map.once("moveend", finish);
  moveFn();
  window.setTimeout(finish, 1600);
}

export function fitMapToOverview(map, userLocation, mapResults, isProgrammaticMoveRef) {
  const origin = getItemLatLng(userLocation);
  if (!map || !origin) return false;

  const closest = getClosestMapVenues(userLocation, mapResults, 10);
  const padding = getMapChromePadding(map);

  runProgrammaticMapMove(
    map,
    () => {
      if (closest.length === 0) {
        map.flyTo([origin.lat, origin.lng], OVERVIEW_FALLBACK_ZOOM, {
          duration: 0.9,
          easeLinearity: 0.25,
        });
        return;
      }

      map.fitBounds(
        [[origin.lat, origin.lng], ...closest.map((point) => [point.lat, point.lng])],
        {
          ...padding,
          maxZoom: OVERVIEW_MAX_ZOOM,
          animate: true,
          duration: 0.9,
        }
      );
    },
    isProgrammaticMoveRef
  );

  return true;
}

export function flyMapToLocation(map, location, isProgrammaticMoveRef) {
  const point = getItemLatLng(location);
  if (!map || !point) return false;

  runProgrammaticMapMove(
    map,
    () => {
      map.flyTo([point.lat, point.lng], OVERVIEW_FALLBACK_ZOOM, {
        duration: 0.9,
        easeLinearity: 0.25,
      });
    },
    isProgrammaticMoveRef
  );

  return true;
}

export function fitMapToNeighborhood(map, item, isProgrammaticMoveRef) {
  const point = getItemLatLng(item);
  if (!map || !point) return false;

  const padding = getMapChromePadding(map);
  runProgrammaticMapMove(
    map,
    () => {
      map.fitBounds(getNeighborhoodBounds(point.lat, point.lng), {
        ...padding,
        maxZoom: NEIGHBORHOOD_MAX_ZOOM,
        animate: true,
        duration: 0.7,
      });
    },
    isProgrammaticMoveRef
  );

  return true;
}

export function getListStickyOffset(root) {
  if (!root) return 0;
  const sticky = root.querySelector(".map-list-desktop-sticky-controls");
  return sticky ? sticky.getBoundingClientRect().height : 0;
}

export function findFirstFullyVisibleListItem(root, itemEls, stickyTop = 0) {
  if (!root || !itemEls?.length) return null;

  const rootRect = root.getBoundingClientRect();
  const topEdge = rootRect.top + stickyTop;
  const bottomEdge = rootRect.bottom;
  const slop = 2;
  const fullyVisible = [];
  const intersecting = [];

  itemEls.forEach((el, fallbackIndex) => {
    const index = Number.isFinite(Number(el.dataset?.mapListIndex))
      ? Number(el.dataset.mapListIndex)
      : fallbackIndex;
    const rect = el.getBoundingClientRect();
    const visibleHeight = Math.min(rect.bottom, bottomEdge) - Math.max(rect.top, topEdge);
    if (visibleHeight <= 0) return;

    const entry = { el, index, rect, visibleHeight };
    const isFully = rect.top >= topEdge - slop && rect.bottom <= bottomEdge + slop;
    if (isFully) fullyVisible.push(entry);
    else intersecting.push(entry);
  });

  const pickTopmost = (list) =>
    [...list].sort((a, b) => a.rect.top - b.rect.top || a.index - b.index)[0] ||
    null;

  return pickTopmost(fullyVisible) || pickTopmost(intersecting);
}
